<template>
  <div class="apps-page">
    <section class="hero">
      <div class="hero-copy">
        <span class="eyebrow">
          <i class="fa-solid fa-mobile-screen-button"></i>
          Aplicativos oficiais
        </span>
        <h1>Leve o sistema com você</h1>
        <p>
          Escolha a plataforma do seu celular para baixar o aplicativo ou
          acessar a versão compatível com iPhone e iPad.
        </p>
      </div>
      <div class="hero-art" aria-hidden="true">
        <div class="phone">
          <div class="phone-camera"></div>
          <div class="phone-platforms">
            <i class="fa-brands fa-android"></i>
            <i class="fa-brands fa-apple"></i>
          </div>
        </div>
        <span class="art-circle circle-one"></span>
        <span class="art-circle circle-two"></span>
      </div>
    </section>

    <section class="download-grid" aria-label="Aplicativos disponíveis">
      <article class="download-card android-card">
        <div class="platform-label android-label">
          <i class="fa-brands fa-android"></i>
          Android
        </div>

        <div class="platform-content">
          <div class="app-icon android-icon">
            <i class="fa-brands fa-android"></i>
          </div>

          <div class="app-details">
            <div class="app-heading">
              <div>
                <h2>Aplicativo Android</h2>
                <p>Instale o app oficial diretamente no seu celular.</p>
              </div>
              <span v-if="latestVersion" class="status-badge">
                <span class="status-dot"></span>
                Versão atual
              </span>
            </div>

            <div v-if="latestVersion" class="version-meta">
              <span>
                <i class="fa-solid fa-code-branch"></i>
                Versão {{ latestVersion.versionName }}
              </span>
              <span>
                <i class="fa-solid fa-hard-drive"></i>
                {{ formatFileSize(latestVersion.fileSize) }}
              </span>
              <span>
                <i class="fa-regular fa-calendar"></i>
                {{ formatDate(latestVersion.releaseDate) }}
              </span>
            </div>

            <div v-if="latestVersion?.releaseNotes" class="release-notes">
              <strong>Novidades desta versão</strong>
              <p>{{ latestVersion.releaseNotes }}</p>
            </div>

            <div v-if="loading" class="inline-state">
              <i class="fa-solid fa-spinner fa-spin"></i>
              Buscando a versão mais recente...
            </div>

            <div v-else-if="loadError" class="inline-state error">
              <i class="fa-solid fa-circle-exclamation"></i>
              <span>Não foi possível carregar o aplicativo.</span>
              <button type="button" @click="loadLatestVersion">Tentar novamente</button>
            </div>

            <div v-else class="actions">
              <button
                type="button"
                class="primary-action android-action"
                :disabled="downloading || !latestVersion"
                @click="downloadAndroidApp"
              >
                <i :class="downloading ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'"></i>
                {{ downloading ? "Preparando download..." : "Baixar para Android" }}
              </button>
              <button
                type="button"
                class="secondary-action"
                :disabled="copyingAndroid"
                @click="copyAndroidLink"
              >
                <i :class="copyingAndroid ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
                {{ copyingAndroid ? "Copiado!" : "Copiar Link" }}
              </button>
            </div>
          </div>
        </div>
      </article>

      <article class="download-card ios-card">
        <div class="platform-label ios-label">
          <i class="fa-brands fa-apple"></i>
          iOS
        </div>

        <div class="platform-content">
          <div class="app-icon ios-icon">
            <i class="fa-brands fa-apple"></i>
          </div>

          <div class="app-details">
            <div class="app-heading">
              <div>
                <h2>Acesso para iOS</h2>
                <p>Use a versão web otimizada no iPhone ou iPad.</p>
              </div>
              <span class="status-badge">
                <span class="status-dot"></span>
                Disponível
              </span>
            </div>

            <div class="ios-tip">
              <i class="fa-solid fa-arrow-up-from-bracket"></i>
              <div>
                <strong>Adicione à Tela de Início</strong>
                <p>
                  Abra o link no Safari, toque em Compartilhar e selecione
                  “Adicionar à Tela de Início”.
                </p>
              </div>
            </div>

            <div class="actions">
              <a
                class="primary-action ios-action"
                :href="iosAppUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="fa-brands fa-apple"></i>
                Abrir no iOS
                <i class="fa-solid fa-arrow-up-right-from-square external-icon"></i>
              </a>
              <button
                type="button"
                class="secondary-action"
                :disabled="copyingIos"
                @click="copyIosLink"
              >
                <i :class="copyingIos ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
                {{ copyingIos ? "Copiado!" : "Copiar Link" }}
              </button>
            </div>
          </div>
        </div>
      </article>
    </section>

    <aside class="security-notice">
      <i class="fa-solid fa-shield-halved"></i>
      <div>
        <strong>Instalação segura fora da Play Store</strong>
        <p>
          O Android pode solicitar permissão para instalar aplicativos de
          fontes desconhecidas. Isso é esperado; autorize somente para concluir
          a instalação deste app oficial.
        </p>
      </div>
    </aside>
  </div>
</template>

<script>
import { http } from "@/services/config";
import { toast } from "@/composables/useToast";

const IOS_APP_URL = "https://app.ralphsys.com/";

export default {
  name: "DownloadApps",
  data() {
    return {
      latestVersion: null,
      loading: true,
      loadError: false,
      downloading: false,
      copyingAndroid: false,
      copyingIos: false,
      iosAppUrl: IOS_APP_URL,
    };
  },
  computed: {
    /** Página pública de download do APK (ViewDownload) */
    androidDownloadPageUrl() {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      return `${origin}/download`;
    },
  },
  mounted() {
    this.loadLatestVersion();
  },
  methods: {
    async loadLatestVersion() {
      this.loading = true;
      this.loadError = false;

      try {
        const response = await http.get("/api/v1/app/version/latest");
        this.latestVersion = response.data;
        this.loadError = !this.latestVersion?.downloadUrl;
      } catch {
        this.latestVersion = null;
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    downloadAndroidApp() {
      if (!this.latestVersion?.downloadUrl || this.downloading) {
        return;
      }

      this.downloading = true;
      const link = document.createElement("a");
      link.href = this.latestVersion.downloadUrl;
      link.download =
        this.latestVersion.downloadUrl.split("/").pop() ||
        `ABApp_v${this.latestVersion.versionName}.apk`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        this.downloading = false;
      }, 1500);
    },
    async copyText(text) {
      if (!text) {
        throw new Error("Link vazio");
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      const ok = document.execCommand("copy");
      input.remove();
      if (!ok) {
        throw new Error("Falha ao copiar");
      }
    },
    async copyAndroidLink() {
      try {
        await this.copyText(this.androidDownloadPageUrl);
        this.copyingAndroid = true;
        toast.success("Link de download copiado!", { duration: 2500 });
        window.setTimeout(() => {
          this.copyingAndroid = false;
        }, 2000);
      } catch {
        toast.error("Não foi possível copiar o link.");
      }
    },
    async copyIosLink() {
      try {
        await this.copyText(this.iosAppUrl);
        this.copyingIos = true;
        toast.success("Link do app iOS copiado!", { duration: 2500 });
        window.setTimeout(() => {
          this.copyingIos = false;
        }, 2000);
      } catch {
        toast.error("Não foi possível copiar o link.");
      }
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("pt-BR");
    },
    formatFileSize(bytes) {
      const size = Number(bytes);
      if (!Number.isFinite(size) || size <= 0) return "-";
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.apps-page {
  --apps-accent: var(--accent-color, #2563eb);
  --apps-card: var(--card-background, var(--card-bg, #fff));
  max-width: 1180px;
  margin: 0 auto;
  padding: 1.5rem;
  color: var(--text-primary);
}

.hero {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(240px, 0.6fr);
  align-items: center;
  min-height: 245px;
  overflow: hidden;
  padding: 2.5rem 3rem;
  border-radius: 24px;
  color: #fff;
  background:
    radial-gradient(circle at 80% 15%, rgba(255, 255, 255, 0.2), transparent 24%),
    linear-gradient(135deg, #172554 0%, #1d4ed8 52%, #06b6d4 100%);
  box-shadow: 0 22px 50px rgba(30, 64, 175, 0.22);
}

.hero-copy {
  position: relative;
  z-index: 2;
  max-width: 670px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.hero h1 {
  margin: 0 0 0.75rem;
  font-size: clamp(2rem, 4vw, 3.35rem);
  line-height: 1.05;
  font-weight: 800;
}

.hero p {
  max-width: 610px;
  margin: 0;
  color: rgba(255, 255, 255, 0.84);
  font-size: 1.05rem;
  line-height: 1.65;
}

.hero-art {
  position: relative;
  display: grid;
  place-items: center;
  min-height: 190px;
}

.phone {
  position: relative;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 112px;
  height: 190px;
  border: 6px solid rgba(255, 255, 255, 0.85);
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.65);
  box-shadow: 0 24px 40px rgba(15, 23, 42, 0.35);
  transform: rotate(7deg);
}

.phone i {
  font-size: 2.5rem;
}

.phone-platforms {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.phone-camera {
  position: absolute;
  top: 8px;
  width: 34px;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.55);
}

.art-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

.circle-one {
  width: 180px;
  height: 180px;
}

.circle-two {
  right: -55px;
  bottom: -80px;
  width: 210px;
  height: 210px;
}

.download-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  margin-top: 1.5rem;
}

.download-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 470px;
  overflow: hidden;
  border: 1px solid var(--border-color);
  border-radius: 20px;
  background: var(--apps-card);
  box-shadow: var(--shadow-md, 0 12px 35px rgba(15, 23, 42, 0.08));
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.download-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.13);
}

.platform-label {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  align-self: flex-start;
  margin: 1rem 1rem 0;
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 700;
}

.android-label {
  background: linear-gradient(135deg, #16a34a, #15803d);
}

.ios-label {
  background: linear-gradient(135deg, #475569, #0f172a);
}

.platform-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 1.15rem;
  padding: 1.35rem 1.5rem 1.5rem;
  text-align: center;
}

.app-icon {
  display: grid;
  place-items: center;
  width: 92px;
  height: 92px;
  border-radius: 23px;
  color: #fff;
  font-size: 2.75rem;
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.16);
}

.android-icon {
  background: linear-gradient(135deg, #22c55e, #15803d);
}

.ios-icon {
  background: linear-gradient(135deg, #334155, #020617);
}

.app-details {
  display: flex;
  flex-direction: column;
  align-self: stretch;
  flex: 1;
  min-width: 0;
}

.app-heading {
  display: block;
  text-align: center;
}

.app-heading h2 {
  margin: 0 0 0.45rem;
  color: var(--text-primary);
  font-size: 1.35rem;
  line-height: 1.25;
}

.app-heading > div > p {
  max-width: 390px;
  margin-inline: auto;
}

.app-heading p,
.ios-tip p,
.security-notice p {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.55;
}

.status-badge {
  position: absolute;
  top: 1.15rem;
  right: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
  padding: 0.4rem 0.65rem;
  border-radius: 999px;
  color: #15803d;
  background: rgba(34, 197, 94, 0.1);
  font-size: 0.75rem;
  font-weight: 700;
}

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.12);
}

.version-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem 1.25rem;
  margin-top: 1.2rem;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.version-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.release-notes,
.ios-tip {
  width: 100%;
  box-sizing: border-box;
  margin-top: 1.25rem;
  padding: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--bg-secondary);
}

.release-notes strong,
.ios-tip strong,
.security-notice strong {
  display: block;
  margin-bottom: 0.3rem;
  color: var(--text-primary);
}

.release-notes p {
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.55;
  white-space: pre-line;
}

.ios-tip {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  text-align: left;
}

.ios-tip > i {
  margin-top: 0.15rem;
  color: var(--apps-accent);
  font-size: 1.25rem;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 0.65rem;
  margin-top: auto;
  padding-top: 1.35rem;
}

.primary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  min-height: 48px;
  padding: 0.75rem 1.2rem;
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 750;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.secondary-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  min-height: 48px;
  padding: 0.75rem 1.1rem;
  border: 1.5px solid var(--border-color, #cbd5e1);
  border-radius: 12px;
  color: var(--text-primary);
  background: var(--apps-card);
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.secondary-action:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: var(--apps-accent);
  color: var(--apps-accent);
}

.secondary-action:disabled {
  opacity: 0.85;
  cursor: default;
}

.primary-action:hover:not(:disabled) {
  transform: translateY(-2px);
}

.primary-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.android-action {
  background: linear-gradient(135deg, #16a34a, #15803d);
  box-shadow: 0 10px 22px rgba(22, 163, 74, 0.23);
}

.ios-action {
  background: linear-gradient(135deg, #334155, #0f172a);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.22);
}

.external-icon {
  font-size: 0.75rem;
  opacity: 0.7;
}

.inline-state {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 1.25rem;
  color: var(--text-secondary);
}

.inline-state.error {
  flex-wrap: wrap;
  color: var(--danger-color, #dc2626);
}

.inline-state button {
  border: 0;
  color: var(--apps-accent);
  background: transparent;
  font-weight: 700;
  cursor: pointer;
}

.security-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  margin-top: 1.25rem;
  padding: 1rem 1.15rem;
  border: 1px solid rgba(14, 165, 233, 0.3);
  border-radius: 14px;
  background: rgba(14, 165, 233, 0.08);
}

.security-notice > i {
  margin-top: 0.15rem;
  color: #0284c7;
  font-size: 1.2rem;
}

@media (max-width: 760px) {
  .apps-page {
    padding: 1rem;
  }

  .hero {
    grid-template-columns: 1fr;
    padding: 2rem 1.5rem;
  }

  .hero-art {
    display: none;
  }

  .download-grid {
    grid-template-columns: 1fr;
  }

  .platform-content {
    padding: 1.25rem;
  }

  .app-icon {
    width: 72px;
    height: 72px;
    border-radius: 18px;
    font-size: 2.1rem;
  }

  .download-card {
    min-height: 0;
  }

  .status-badge {
    position: static;
    margin: 0.85rem auto 0;
  }

  .primary-action,
  .secondary-action {
    width: 100%;
  }
}
</style>
